import React from "react";
import LegalLayout, { LegalH2, LegalP, LegalUL } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testId="privacy-policy-page"
      overline="Your Data, Your Trust"
      title="Privacy Policy"
      updated="February 2026"
    >
      <LegalP>
        MileForge (a brand of <strong>Touring Craft &amp; Riders Cafe</strong>, GSTIN
        {" "}<strong>33AIUPC4644A1ZL</strong>, based in Chennai, Tamil Nadu, India) respects your privacy.
        This policy explains what information we collect when you use mileforge.in, how we use it,
        who we share it with, and the choices you have.
      </LegalP>

      <LegalH2>1. Information We Collect</LegalH2>
      <LegalUL>
        <li><strong>Identification data:</strong> Name, email, phone number, shipping/billing address — provided during registration, checkout, or contact.</li>
        <li><strong>Transaction data:</strong> Order details, products purchased, order value, and payment method. Payment credentials (card/UPI) are handled directly by our payment aggregator (Cashfree) and never stored on our servers.</li>
        <li><strong>Technical data:</strong> IP address, browser type, device details, referring URLs, and pages visited — collected automatically via cookies and analytics.</li>
        <li><strong>Communication data:</strong> Any messages, feedback, reviews, or support tickets you send us.</li>
      </LegalUL>

      <LegalH2>2. How We Use Your Information</LegalH2>
      <LegalUL>
        <li>To process and fulfil your orders — including shipping, invoicing, and delivery updates.</li>
        <li>To create and manage your customer account.</li>
        <li>To send transactional emails such as order confirmations, shipping updates, and password resets.</li>
        <li>To send marketing communications about new products, drops, and offers — <em>only if you opt in</em>. You can unsubscribe any time.</li>
        <li>To respond to your queries and provide customer support.</li>
        <li>To detect and prevent fraud, abuse, and security incidents.</li>
        <li>To comply with legal obligations under Indian law.</li>
      </LegalUL>

      <LegalH2>3. Who We Share Your Data With</LegalH2>
      <LegalP>
        We do not sell or rent your personal data. We share it only with trusted third-party service
        providers who help us operate the business, under strict confidentiality obligations:
      </LegalP>
      <LegalUL>
        <li><strong>Payment Aggregator:</strong> Cashfree Payments India Pvt. Ltd. (PCI-DSS compliant).</li>
        <li><strong>Shipping Partners:</strong> Delhivery, Shiprocket, India Post, and other courier services for order fulfilment.</li>
        <li><strong>Email Delivery:</strong> Resend (for transactional emails such as order confirmations and password resets).</li>
        <li><strong>Cloud Hosting:</strong> Our infrastructure runs on secure cloud providers with encryption at rest.</li>
        <li><strong>Analytics:</strong> Aggregated, non-identifying usage data may be processed via Google Analytics or similar tools.</li>
        <li><strong>Legal Requests:</strong> We may disclose data if legally compelled by a court or government authority.</li>
      </LegalUL>

      <LegalH2>4. Cookies</LegalH2>
      <LegalP>
        We use cookies to remember your cart, keep you signed in, and understand how visitors use the
        Website. You can disable cookies in your browser settings, but some features (such as
        cart persistence and checkout) may stop working.
      </LegalP>

      <LegalH2>5. Data Retention</LegalH2>
      <LegalP>
        We retain your data for as long as your account is active or as required to provide services,
        comply with legal obligations, resolve disputes, and enforce our agreements. Order records
        are retained for a minimum of 7 years to comply with Indian tax and accounting laws.
      </LegalP>

      <LegalH2>6. Data Security</LegalH2>
      <LegalUL>
        <li>All traffic to and from mileforge.in is encrypted with HTTPS/TLS.</li>
        <li>Passwords are hashed using industry-standard bcrypt — we never store your password in plain text.</li>
        <li>Session tokens are stored in httpOnly, secure cookies to prevent XSS-based theft.</li>
        <li>Payment data is handled exclusively by Cashfree and never touches our servers.</li>
      </LegalUL>

      <LegalH2>7. Your Rights</LegalH2>
      <LegalP>
        Under the applicable data protection laws of India (including the DPDP Act 2023), you have
        the right to:
      </LegalP>
      <LegalUL>
        <li>Access the personal data we hold about you.</li>
        <li>Request correction of inaccurate or incomplete data.</li>
        <li>Request deletion of your data (subject to legal retention requirements).</li>
        <li>Withdraw consent to marketing communications at any time.</li>
        <li>Lodge a complaint with the Data Protection Board of India.</li>
      </LegalUL>
      <LegalP>
        To exercise any of these rights, email us at
        {" "}<a href="mailto:hello@mileforge.in" className="text-[#D96A1B] hover:underline">hello@mileforge.in</a>.
      </LegalP>

      <LegalH2>8. Children's Privacy</LegalH2>
      <LegalP>
        Our services are not intended for individuals under 18. We do not knowingly collect data
        from minors. If you believe a minor has provided us data, please contact us and we will
        delete it.
      </LegalP>

      <LegalH2>9. International Transfers</LegalH2>
      <LegalP>
        Some of our service providers (e.g. email delivery) may process data on servers outside India.
        In such cases, we ensure the transfer is protected by appropriate contractual safeguards.
      </LegalP>

      <LegalH2>10. Changes to This Policy</LegalH2>
      <LegalP>
        We may update this policy from time to time. The updated version will be posted on this page
        with a revised "Last updated" date. For material changes, we may notify you via email.
      </LegalP>

      <LegalH2>11. Contact Our Data Officer</LegalH2>
      <LegalP>
        Questions or concerns about your data? Write to
        {" "}<a href="mailto:hello@mileforge.in" className="text-[#D96A1B] hover:underline">hello@mileforge.in</a>
        {" "}with "Privacy" in the subject line.
      </LegalP>
    </LegalLayout>
  );
}
